import React, { Fragment, useCallback, useEffect, useState } from 'react';

import { App, Button, Pagination, Switch, Table, Tooltip, Typography } from 'antd';

import Truncate from 'components/Truncate';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import { useListProductCampaigns, useEditProductCampaign } from 'v2/hooks/campaigns';
import type { CampaignLinkType } from 'v2/services/campaigns';
import { useUserStore } from 'v2/store/User';

import { Icon } from '@eduzz/ui-antd-theme';

import type { ICheckoutsUrls } from '../..';
import ClipboardField from '../ClipboardField';

const TAB_TO_LINK_TYPE: Record<string, CampaignLinkType> = {
  'producer': 'producerLinks',
  'affiliate': 'affiliatesLinks',
  'my-links': 'myLinks'
};

interface IProps {
  type: 'producer' | 'affiliate' | 'eduzz' | 'my-links';
  openUrlCreator: (campaign: string) => void;
  openUrlCreatorCheckout: (urls: ICheckoutsUrls) => void;
  openQuickView?: any;
  isProducer?: boolean;
  setUpdate: any;
  productId: number;
}

const CampaignTable: React.FC<IProps> = ({ type, openUrlCreator, openQuickView, isProducer, setUpdate, productId }) => {
  const { message } = App.useApp();
  const [pagination, setPagination] = useState({ current_page: 1, per_page: 15 });

  const linkType = TAB_TO_LINK_TYPE[type] ?? 'producerLinks';

  const {
    data: campaignsResponse,
    isLoading: isLoadingCampaigns,
    refetch
  } = useListProductCampaigns(productId, linkType, pagination);
  const editMutation = useEditProductCampaign();

  const campaigns = campaignsResponse?.data ?? [];
  const totalItems = campaignsResponse?.pagination?.total_items ?? 0;

  const { currentUserId } = useUserStore(({ user }) => ({ currentUserId: user.id }));

  useEffect(() => {
    setUpdate(() => refetch());
  }, [setUpdate, refetch]);

  const handleChangeStatusCampaign = useCallback(
    async (idCampaign: number, status: boolean) => {
      try {
        await editMutation.mutateAsync({ productId, campaignId: idCampaign, data: { active: status } });
        message.success('Status atualizado com sucesso');
      } catch (error) {
        message.error('Ops, erro na alteração de status');
      }
    },
    [productId, editMutation, message]
  );

  const handleClickStatusCampaignAffiliate = useCallback(
    async (idCampaign: number, status: string) => {
      try {
        await editMutation.mutateAsync({ productId, campaignId: idCampaign, data: { status } });
        message.success('Status atualizado com sucesso');
      } catch (error) {
        message.error('Ops, erro na alteração de status do afiliado');
      }
    },
    [productId, editMutation, message]
  );

  const onChangePage = useCallback((page: number) => {
    setPagination(prev => ({ ...prev, current_page: page }));
  }, []);

  const onChangeRowsPerPage = useCallback((_current: number, pageSize: number) => {
    setPagination(prev => ({ ...prev, per_page: pageSize, current_page: 1 }));
  }, []);

  const columns = [
    {
      title: 'Status',
      key: 'status',
      width: 80,
      render: (_: any, item: any) => (
        <div className='flex items-center'>
          <Tooltip title={item.active ? 'Ativo' : 'Inativo'} placement='top'>
            <div>
              <Switch
                checked={item.active}
                onClick={() => handleChangeStatusCampaign(item.id, item.active ? false : true)}
                disabled={type === 'affiliate' || (type === 'producer' && !isProducer)}
                size='small'
              />
            </div>
          </Tooltip>
          {type === 'my-links' && item.status === 'R' && (
            <Tooltip
              placement='top'
              title='Essa campanha foi reprovada pelo produtor e o redirecionamento será feito para a página de venda padrão da Eduzz'
            >
              <Icon.TriangleAlert className='ml-2 text-(--uizz-antd-color-error)' size={16} />
            </Tooltip>
          )}
        </div>
      )
    },
    {
      title: 'Chave',
      dataIndex: 'key',
      key: 'key',
      width: 80,
      render: (key: string) => <Typography.Text type='secondary'>{key}</Typography.Text>
    },
    {
      title: 'FI',
      key: 'fi',
      width: 60,
      render: (_: any, item: any) =>
        item.infinity_funnel ? (
          <Tooltip placement='top' title='Verificado'>
            <img width={24} src='/img/icon_funil_ativo.png' alt='Funil' />
          </Tooltip>
        ) : (
          <Tooltip placement='top' title='Não verificado'>
            <img width={24} src='/img/icon_funil.png' alt='Funil' />
          </Tooltip>
        )
    },
    {
      title: 'Campanha',
      key: 'campaign',
      width: 200,
      render: (_: any, item: any) => (
        <Typography.Text>
          <Truncate count='25' final='...'>
            {item.title}
          </Truncate>
        </Typography.Text>
      )
    },
    {
      title: 'URL divulgação',
      key: 'url',
      render: (_: any, item: any) => (
        <div className='grid grid-cols-1 items-center gap-4 md:grid-cols-2'>
          <div>
            <ClipboardField value={`https://edzz.la/${item.key}?a=${currentUserId}`} />
          </div>
          <div>
            <PermissionHide action='myeduzz:links:editar'>
              <Button
                onClick={() => openUrlCreator(`https://edzz.la/${item.key}?a=${currentUserId}`)}
                icon={<Icon.Pencil size={16} />}
              >
                Criador de URL
              </Button>
            </PermissionHide>
          </div>
          <div className='md:col-span-2'>
            <Typography.Text type='secondary' className='mt-2'>
              <Truncate
                count='512'
                final='...'
                after={
                  <Fragment>
                    {!item.valid_url && item.url.length > 0 && (
                      <Tooltip
                        placement='bottom'
                        title='Essa URL não possui script de rastreio validado pela Eduzz. Utilize a URL acima para garantir o ganho de comissões.'
                      >
                        <Typography.Text type='secondary' className='ml-2'>
                          <Icon.Info size={16} />
                        </Typography.Text>
                      </Tooltip>
                    )}
                  </Fragment>
                }
              >
                {`${item.url}?a=${currentUserId}`}
              </Truncate>
            </Typography.Text>
          </div>
        </div>
      )
    },
    {
      title: 'RPC/A',
      dataIndex: 'rpc_affiliate',
      key: 'rpc_affiliate',
      width: 80,
      align: 'right' as const
    },
    {
      title: 'RPC/P',
      dataIndex: 'rpc_producer',
      key: 'rpc_producer',
      width: 80,
      align: 'right' as const
    },
    {
      title: 'Ações',
      key: 'actions',
      align: 'center' as const,
      fixed: 'right' as const,
      width: type === 'affiliate' ? 120 : 60,
      render: (_: any, item: any) => (
        <div className='flex items-center justify-center gap-2'>
          {(type === 'producer' || type === 'my-links') && (
            <Button
              type='text'
              shape='circle'
              icon={<Icon.Ellipsis />}
              aria-label='Ações'
              onClick={() => openQuickView(item)}
            />
          )}
          {item.can_moderate && type === 'affiliate' && (
            <div className='flex'>
              <Button
                type='text'
                shape='circle'
                color='green'
                disabled={item.status === 'A'}
                onClick={() => handleClickStatusCampaignAffiliate(item.id, 'A')}
                icon={<Icon.ThumbsUp size={16} />}
              />
              <Button
                type='text'
                shape='circle'
                danger
                disabled={item.status === 'R'}
                onClick={() => handleClickStatusCampaignAffiliate(item.id, 'R')}
                icon={<Icon.ThumbsDown size={16} />}
              />
            </div>
          )}
        </div>
      )
    }
  ];
  return (
    <div>
      <div>
        {type !== 'eduzz' && (
          <>
            <Table
              size='small'
              sticky
              className='[&_.ant-table-tbody_.ant-typography]:text-sm'
              scroll={{ x: 'max-content' }}
              columns={columns}
              dataSource={campaigns}
              rowKey={(item: any) => `cpg${item.id}`}
              loading={isLoadingCampaigns}
              pagination={false}
            />
            {!isLoadingCampaigns && (
              <div className='mt-4 flex justify-end'>
                <Pagination
                  current={pagination.current_page}
                  pageSize={pagination.per_page}
                  total={totalItems}
                  onChange={onChangePage}
                  onShowSizeChange={onChangeRowsPerPage}
                  showSizeChanger
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CampaignTable;
